import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { updateMasterMetadata } from "../utils/cacheManager";

function CdmMaster() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [cdmProducts, setCdmProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [officialClientId, setOfficialClientId] = useState("");
  
  // Bulk States
  const [showBulk, setShowBulk] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  const [form, setForm] = useState({
    clientId: "",
    category: "",
    division: "",
    productName: "",
    msku: "",
    rate: "",
  });

  // Fetch Official Client ID
  const fetchClientId = async () => {
    try {
      const snap = await getDoc(doc(db, "companyInfo", "default"));
      if (snap.exists()) {
        const cid = snap.data().clientId || "";
        setOfficialClientId(cid);
        setForm(f => ({ ...f, clientId: cid }));
      }
    } catch (err) {
      console.error("Error fetching clientId:", err);
    }
  };

  // Load CDM Products
  const fetchCdmProducts = async () => {
    if (!officialClientId) return;
    try {
      const q = query(
        collection(db, "cdmMaster"),
        where("clientId", "==", officialClientId)
      );
      const querySnapshot = await getDocs(q);
      const list = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCdmProducts(list);
    } catch (err) {
      console.error("Error fetching cdmMaster:", err);
    }
  };

  useEffect(() => {
    fetchClientId();
  }, []);

  useEffect(() => {
    if (officialClientId) {
      fetchCdmProducts();
    }
  }, [officialClientId]);

  // Handle form change
  const updateForm = (e) => {
    if (e.target.name === "clientId") return;
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Save Product
  const saveProduct = async () => {
    try {
      if (!form.productName || form.productName.trim() === "") {
        alert("❌ Product Name is required.");
        return;
      }
      if (!form.msku || form.msku.trim() === "") {
        alert("❌ MSKU is required.");
        return;
      }
      if (!form.rate || isNaN(form.rate)) {
        alert("❌ Valid Rate is required.");
        return;
      }

      const payload = {
        ...form,
        clientId: officialClientId,
        rate: Number(form.rate || 0),
        category: (form.category || "").toUpperCase(),
        division: (form.division || "").toUpperCase(),
        msku: (form.msku || "").toUpperCase()
      };

      if (editingId) {
        await updateDoc(doc(db, "cdmMaster", editingId), payload);
      } else {
        await addDoc(collection(db, "cdmMaster"), payload);
      }
      
      setForm({
        clientId: officialClientId,
        category: "",
        division: "",
        productName: "",
        msku: "",
        rate: "",
      });

      setEditingId(null);
      await updateMasterMetadata(db, "cdm");
      fetchCdmProducts();
    } catch (err) {
      console.error("Error saving CDM Product:", err);
    }
  };

  // Edit Product
  const editProduct = (prod) => {
    setEditingId(prod.id);
    const { id, ...rest } = prod;
    setForm({
      ...rest,
      rate: String(rest.rate || "")
    });
  };

  // Delete Product
  const removeProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this CDM Product?")) return;
    await deleteDoc(doc(db, "cdmMaster", id));
    await updateMasterMetadata(db, "cdm");
    fetchCdmProducts();
  };

  // --- BULK OPERATIONS ---
  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").map((x) => x.trim()).filter(Boolean);
      const headers = lines[0].split(",");
      const rows = lines.slice(1).map((line) => {
        const cols = line.split(",");
        const obj = {};
        headers.forEach((h, i) => {
          obj[h.trim()] = cols[i] ? cols[i].trim() : "";
        });
        return obj;
      });
      setCsvData(rows);
    };
    reader.readAsText(file);
  };

  const confirmUpload = async () => {
    setBulkLoading(true);
    try {
      for (const row of csvData) {
        await addDoc(collection(db, "cdmMaster"), {
          clientId: officialClientId,
          category: (row.category || "").toUpperCase(),
          division: (row.division || "").toUpperCase(),
          productName: row.productName || "",
          msku: (row.msku || "").toUpperCase(),
          rate: Number(row.rate || 0),
        });
      }
      alert("Bulk upload successful!");
      setCsvData([]);
      setShowBulk(false);
      fetchCdmProducts();
      await updateMasterMetadata(db, "cdm");
    } catch (err) {
      console.error(err);
      alert("Error during upload.");
    }
    setBulkLoading(false);
  };

  const downloadTemplate = () => {
    const headers = "clientId,category,division,productName,msku,rate";
    const sampleRow = `${officialClientId},CIGARETTES,WD,Sample Brand MSKU,MSKU101,155.50`;
    const blob = new Blob([headers + "\n" + sampleRow], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cdm_master_template.csv";
    a.click();
  };

  const exportAll = () => {
    const headers = "clientId,category,division,productName,msku,rate";
    const rows = cdmProducts.map((p) => [
      officialClientId, p.category || "", p.division || "", p.productName || "", p.msku || "", p.rate || 0
    ].join(","));
    const csvContent = [headers, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cdm_master_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const filteredProducts = cdmProducts.filter((p) => {
    const term = search.toLowerCase();
    return (
      p.category?.toLowerCase().includes(term) ||
      p.division?.toLowerCase().includes(term) ||
      p.productName?.toLowerCase().includes(term) ||
      p.msku?.toLowerCase().includes(term) ||
      String(p.rate)?.toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          
          {/* HEADER & TOP ACTIONS */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32, flexWrap: "wrap", gap: 20 }}>
            <div style={{ borderLeft: "6px solid #673ab7", paddingLeft: 20 }}>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
                CDM Master
              </h1>
              <p style={{ marginTop: 4, fontSize: 16, color: "#64748b", fontWeight: 500 }}>
                Manage products, divisions, and billing rates for CDM Hawking Sales
              </p>
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={exportAll} style={{ ...secondaryActionStyle, background: "#10b981", color: "#fff" }}>
                📊 Export All
              </button>
              <button onClick={() => setShowBulk(!showBulk)} style={{ ...primaryActionStyle, background: showBulk ? "#f1f5f9" : "linear-gradient(135deg, #673ab7 0%, #512da8 100%)", color: showBulk ? "#475569" : "#fff"  }}>
                {showBulk ? "✖ Close Bulk Mode" : "📦 Bulk Operations"}
              </button>
            </div>
          </div>

          {/* BULK MANAGEMENT DASHBOARD */}
          {showBulk && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 350px", gap: 32, marginBottom: 40, alignItems: "start" }}>
              <div style={{ background: "#ffffff", borderRadius: 16, padding: "32px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #673ab7" }}>
                <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: "18px", fontWeight: 800, color: "#673ab7" }}>CSV BATCH UPLOAD</h3>
                <div style={{ background: "#f8fafc", border: "2px dashed #e2e8f0", borderRadius: 12, padding: "40px 20px", textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: "40px", marginBottom: "16px" }}>📄</div>
                  <input type="file" accept=".csv" onChange={handleCSV} style={{ fontSize: "14px", color: "#673ab7", cursor: "pointer" }} />
                </div>

                {csvData.length > 0 && (
                  <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>AUDIT PREVIEW ({csvData.length} RECORDS)</h3>
                    <div style={{ maxHeight: "250px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 24 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
                          <tr style={{ textAlign: "left" }}>
                            {Object.keys(csvData[0] || {}).map((h) => <th key={h} style={tableHeaderStyle}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {csvData.slice(0, 10).map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              {Object.values(row).map((val, i) => <td key={i} style={{ padding: "12px 16px" }}>{val}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvData.length > 10 && <div style={{ fontSize: "11px", padding: "12px", textAlign: "center", color: "#94a3b8", background: "#f8fafc", fontWeight: 600 }}>... {csvData.length - 10} more records ready</div>}
                    </div>
                    <button onClick={confirmUpload} disabled={bulkLoading} style={{ ...primaryActionStyle, width: "100%", height: "50px" }}>
                      {bulkLoading ? "⚙️ SYNCING CATALOG..." : `✅ COMMIT ${csvData.length} RECORDS TO MASTER`}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ background: "#1e293b", borderRadius: 16, padding: "24px", color: "#f8fafc", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
                <h3 style={{ marginTop: 0, fontSize: "12px", color: "#b39ddb", letterSpacing: "0.1em", fontWeight: 800, textTransform: "uppercase" }}>FORMAT GUIDE</h3>
                <p style={{ fontSize: "13px", color: "#94a3b8", lineHeight: "1.6", margin: "12px 0" }}>Ensure your column headers match exactly to prevent sync errors.</p>
                <div style={{ background: "rgba(255,255,255,0.05)", padding: "12px", borderRadius: 8, fontFamily: "monospace", fontSize: "10px", wordBreak: "break-all" }}>
                  clientId, category, division, productName, msku, rate
                </div>
                <button onClick={downloadTemplate} style={{ ...secondaryActionStyle, width: "100%", marginTop: 16, background: "rgba(255,255,255,0.1)", color: "#fff" }}>
                  📥 Download Template
                </button>
              </div>
            </div>
          )}

          {/* FORM CARD (ONLY IF NOT IN BULK MODE) */}
          {!showBulk && (
            <div style={{ background: "#ffffff", borderRadius: 16, padding: "32px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", marginBottom: 40 }}>
              <h3 style={{ marginTop: 0, marginBottom: 24, fontSize: "18px", fontWeight: 800, color: "#1e293b" }}>
                {editingId ? "✏️ Edit Product Details" : "🆕 Register New CDM Product"}
              </h3>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px 30px" }}>
                {[
                  ["clientId", "Client ID", "mdsons"],
                  ["category", "Category", "e.g. CIGARETTES"],
                  ["division", "Division", "e.g. WD or DS"],
                  ["productName", "Product Name", "e.g. Gold Flake Super Star"],
                  ["msku", "MSKU", "Unique identifier code"],
                  ["rate", "Rate", "e.g. 150.00"]
                ].map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    {key === "clientId" ? (
                      <div style={{ ...inputStyle, background: "#f1f5f9", fontWeight: 700, color: "#673ab7" }}>
                        {officialClientId} 
                        <span style={{ fontSize: "10px", color: "#94a3b8", marginLeft: "8px", fontWeight: "normal" }}>(AUTO)</span>
                      </div>
                    ) : (
                      <input
                        style={inputStyle}
                        name={key}
                        value={form[key]}
                        onChange={updateForm}
                        placeholder={placeholder}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 32, display: "flex", gap: 12 }}>
                <button onClick={saveProduct} style={{ ...primaryActionStyle, width: "100%", height: "50px", fontSize: "16px" }}>
                  {editingId ? "💾 UPDATE PRODUCT RECORD" : "✅ SAVE NEW PRODUCT"}
                </button>
                {editingId && (
                   <button onClick={() => {
                     setEditingId(null);
                     setForm({
                       clientId: officialClientId,
                       category: "",
                       division: "",
                       productName: "",
                       msku: "",
                       rate: "",
                     });
                   }} style={{ ...secondaryActionStyle, padding: "0 24px" }}>
                     Cancel
                   </button>
                )}
              </div>
            </div>
          )}

          {/* CATALOG TABLE */}
          <div style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>CDM PRODUCT CATALOG ({filteredProducts.length})</h3>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Search list..."
                style={{ ...inputStyle, width: "300px", padding: "10px 16px" }}
              />
            </div>

            <div style={{ overflowX: "auto", maxHeight: "65vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={tableHeaderStyle}>PRODUCT NAME</th>
                    <th style={tableHeaderStyle}>MSKU</th>
                    <th style={tableHeaderStyle}>DIVISION</th>
                    <th style={tableHeaderStyle}>CATEGORY</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>RATE</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p, idx) => (
                    <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                      <td style={{ padding: "16px 24px", fontWeight: 800, color: "#1e293b" }}>{p.productName}</td>
                      <td style={{ padding: "16px 24px", color: "#475569", fontWeight: 600 }}>{p.msku}</td>
                      <td style={{ padding: "16px 24px" }}>
                        <span style={{ fontSize: "10px", background: "#f3e5f5", color: "#673ab7", padding: "4px 8px", borderRadius: 4, fontWeight: 800 }}>{p.division}</span>
                      </td>
                      <td style={{ padding: "16px 24px", color: "#64748b" }}>{p.category}</td>
                      <td style={{ padding: "16px 24px", textAlign: "right", fontWeight: 700, color: "#10b981" }}>
                        ₹ {Number(p.rate || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                          <button onClick={() => editProduct(p)} style={tableActionBtnStyle}>Edit</button>
                          <button onClick={() => removeProduct(p.id)} style={{ ...tableActionBtnStyle, color: "#ef4444" }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ padding: 100, textAlign: "center" }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🔎</div>
                        <h3 style={{ color: "#1e293b", margin: 0 }}>No CDM Products Found</h3>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const primaryActionStyle = {
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #673ab7 0%, #512da8 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(103, 58, 183, 0.3)",
};

const secondaryActionStyle = {
  ...primaryActionStyle,
  width: "auto",
  background: "#f1f5f9",
  color: "#475569",
  boxShadow: "none",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  fontSize: "14px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 800,
  color: "#94a3b8",
  marginBottom: "8px",
  letterSpacing: "0.05em",
  textTransform: "uppercase"
};

const tableHeaderStyle = {
  padding: "16px 24px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0"
};

const tableActionBtnStyle = {
  background: "none",
  border: "none",
  color: "#673ab7",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  padding: "4px 8px"
};

export default CdmMaster;
